/**
* DisplaySystemController schedules content for one display. In production,
* the application will run only one instance. In simulation, several displays
* can be simulated simultaneously.
**/
import moment from "moment";
import {TemplateBlock} from '../../model/model';
import logo from '../../assets/logo-drm.png';

class DisplaySystemController {
  constructor($element, $q, $scope, $timeout, APIConfig, appModel, appServices, utils) {
    "ngInject";
    this.$element = $element;
    this.$q = $q;
    this.$scope = $scope;
    this.$timeout = $timeout;
    this.APIConfig = APIConfig;
    this.appModel = appModel;
    this.appServices = appServices;
    this.utils = utils;
    /* Current scenario template plan. */
    this.currentSTP = null;
    this.systemInfo = null;
    this.nSystemStyle = null;
  }
  $onInit() {
    this.$scope.$watch('this.$element', $element=> {
    	if ($element!= undefined && $element[0] != undefined) {
    	  const element = $element[0];
          if (element) {
            const unscaled = element.getBoundingClientRect();
            const scaleFactorHeight = window.innerHeight / unscaled.height;
            const scaleFactorWidth = window.innerWidth / unscaled.width;
            const scale = Math.min(scaleFactorHeight, scaleFactorWidth);
            this.nSystemStyle = {transform: `scale(${scale})`};
          }
    	}
    });
    this.poll();
  }

  getSystemInfo() {
    if (this.appModel.systemInfo && this.appModel.userInfo) {
      return this.$q.resolve();
    } else {
      return this.appServices.login()
      .catch(error => {
        this.appModel.setError('displaySystem.controller', this.APIConfig.error.LOGIN_ERROR, this.APIConfig.errorLevel.BLOCKING);
        return this.$q.reject(error);
      });
    }
  }

  poll() {
    console.debug('poll()');
    this.appModel.clearPendingErrors();
    this.getSystemInfo()
      .then(() => {
        this.getSchedule()
          .finally(() => {
            this.schedulePoll();
            this.appModel.processPendingErrors();
          });
      })
      .catch(() => {
        this.schedulePoll();
        this.appModel.processPendingErrors()
      });
  }

  schedulePoll() {
    this.$timeout(() => {
      this.poll();
    }, this.APIConfig.scenarioTemplatePlanPollInterval);
  }

  getSchedule() {
    const deferred = this.$q.defer();
    const lookAheadSeconds = 10;
    let interval = this.getTimeFrame(lookAheadSeconds);
    /* Get the overall schedule. This is the 'big picture'. */
    this.appServices.getScenarioTemplatePlan(interval.from, interval.to)
      .then(result => {
        const stpHasChanged = !this.schedule || this.schedule.lastUpdate !== result.lastUpdate;
        this.schedule = result;

        /* Get the week scenario with the highest priority/level. */
        const currentSTP = this.getCurrentSTP(result.scenarioTemplatePlan, interval.from);
        const stpSwitch = !this.currentSTP || this.currentSTP.id !== currentSTP.id;
        this.currentSTP = currentSTP;
        if (this.currentSTP) {
          /* Cache all media that is assigned to the current system within a certain time frame. */
          this.cacheMedia(this.schedule.scenarioBlockIds, lookAheadSeconds);

          /* Get all articles within the time frame. If the scenario template plan has changed,
             get the template that corresponds to the current week scenario.
           */
          const promises = [];
          const scenarioBlockIds = this.getScenarioBlockIds(this.schedule.weekSchedule);
          promises.push(this.getArticles(interval.from, interval.to, scenarioBlockIds));
          if (stpHasChanged || stpSwitch) {
            promises.push(this.getTemplate(this.currentSTP.templateId));
            /* remove all articles from existing template blocks */
            if (this.currentTemplate) {
              this.currentTemplate.templateBlocks.forEach(templateBlock => {
                templateBlock.articles = [];
              });
            }
          }
          this.$q.all(promises)
            .then(() => {
              this.allocateArticles(this.articles, this.currentTemplate);
              deferred.resolve(this.APIConfig.message.ARTICLES_SCHEDULED);
            })
            .catch(error => {
              deferred.reject(error);
            });
        } else {
          /* Display the default screen in case we have no current week scenario. */
          this.gotoDefaultScreen();
          deferred.resolve(this.APIConfig.message.NO_CURRENT_WEEK_SCENARIO);
        }
      })
      .catch(error => {
        deferred.reject(error);
      });
    return deferred.promise;
  }

  getScenarioBlockIds(weekSchedule) {
    let ids = [];
    if (weekSchedule) {
      weekSchedule.forEach(schedule => {
        schedule.dayscenarios.forEach(dayScenario => {
          ids = ids.concat(dayScenario.scenarioBlocks.map(block => block.code));
        });
      })
    }
    return _.uniq(ids);
  }

  /**
  * Get from and to date used for filtering
  **/
  getTimeFrame(seconds) {
    const from = moment();
    const to = moment().add(seconds, 'seconds');
    return {
      from,
      to
    }
  }
  /**
  * Cache images (as base64)
  **/
  cacheMedia(scenarioBlockIds, days) {

  }

  /**
  * Find the last scenario that matches the current date.
  **/
  getCurrentSTP(stps, now) {
    const scenario = _.findLast(stps, stp => {
      return this.utils.isBetweenDates(stp._from, stp._to, now);
      // return now.diff(stp._from) > 0 && now.diff(stp._to) < 0;
    });

    return scenario;
  }

  getArticles(from, to, scenarioBlockIds) {
    return this.appServices.getArticles(from, to, scenarioBlockIds)
      .then(result => {
        this.articles = result;
      })
  }

  getTemplate(templateId) {
    return this.appServices.getTemplates(templateId)
      .then(result => {
        this.currentTemplate = _.find(result, {id: templateId});
        if (!this.currentTemplate) {
          this.appModel.setError('displaySystem.controller', this.APIConfig.error.RECEIVED_INCORRECT_DATA_FORMAT, this.APIConfig.errorLevel.BLOCKING);
        }
      })
  }
  gotoDefaultScreen() {
    this.currentTemplate = null;
    this.articles = null;
  }

  /**
  * Distribute the articles over the template blocks. Remove articles from
  * template blocks that are not part of articles. All articles are either
  * new or have been updated since the polling.
  **/
  allocateArticles(articles, template) {
    if (articles && template) {
      /* Refresh the list of articles per template block. */
       const id2Article = _.keyBy(articles, 'id');
       const allocatedArticleIds = [];
       template.templateBlocks.forEach(templateBlock => {
         const blockArticles = [];
         templateBlock.articles.forEach(article => {
           /* If the already allocated article is in the new list of articles,
              then keep article allocated to the same templateBlock. */
           if (id2Article[article.id]) {
             blockArticles.push(id2Article[article.id]);
             allocatedArticleIds.push(article.id);
           }
         });
         /* Reassign the articles to the template block. Articles that
            are no longer in the current list of articles are thus removed. */
         templateBlock.articles = blockArticles;
       });

       /* Allocate all not allocated articles. */
       articles
        .filter(article => allocatedArticleIds.indexOf(article.id) === -1)
        .forEach(article => {
          /* Select template blocks with an equal or lower level.
            Then pick the block with the least number of articles.
            We don't take into account if the articles are already active.
           */
          let templateBlocks = _
            .chain(template.templateBlocks)
            .filter(templateBlock => {
              /* Filter on contentType and position */
              return templateBlock.contentType.indexOf(article.contentType) !== -1 &&
                templateBlock.position === article.position;
            })
            .filter(templateBlock => {
              /* Filter on levels <= article.level */
              return templateBlock.level <= article.level;
            })
            .value();

          if (templateBlocks.length > 0) {
            /* Get the highest allowed level */
            const maxLevel = _.maxBy(templateBlocks, 'level').level;
            const templateBlock = _
              .chain(templateBlocks)
              .filter(templateBlock => templateBlock.level === maxLevel)
              .minBy(templateBlock => {
                return templateBlock.articles.length;
              })
              .value();

            if (templateBlock) {
              templateBlock.articles = templateBlock.articles || [];
              templateBlock.articles.push(article);
            }
          }
        })
      }
    }

    /* Layout/template functions */
    get systemStyle() {
      /* Get  */
      return this.nSystemStyle;
      
    }
    set systemStyle(nStyle) {
      /* set */
      this.nSystemStyle = nStyle;
      return nStyle;
    }

    get operationMode() {
      return this.appModel.getAggregatedOperationMode();
    }

    get hasFallBackImage() {
      return _.has(this.appModel, 'systemInfo.fallbackImage');
    }

    getFallBackStyle() {
      let style = {};
      if (this.hasFallBackImage) {
        const base64 = this.appModel.systemInfo.fallbackImage;
        const image = this.utils.getBase64Src(base64);

        style = {
          'background-image': `url(${image})`
        }
      }

      /* Set height */
      const height = this.currentTemplate ? this.currentTemplate.screenresolution.height: 500;
      style['min-height'] = `${height}px`;

      /* Set width */
      if (this.currentTemplate) {
        style.width = this.currentTemplate.screenresolution.width;
      }
      return style;
    }

    get defaultImage() {
      return logo;
    }
}

export default DisplaySystemController;



// WEBPACK FOOTER //
// ./client/app/components/displaySystem/displaySystem.controller.js